import React from "react";
import {
	GitMerge,
	GitPullRequestClosed,
	MessageSquare,
	User,
} from "lucide-react";
import { formatProjectName, isStale, mrLinkUrl } from "../utils";
import { useCodiffLinks } from "../hooks/useCodiffLinks";
import { needsReviewerFollowUp } from "../hooks/reviewerThreads";
import { PipelineStatus } from "./PipelineStatus";
import { ApprovalStatus } from "./ApprovalStatus";
import { Avatar } from "./Avatar";
import { Tooltip } from "./Tooltip";
import { DateTime } from "./DateTime";
import { MRStatusIcons, DraftIcon } from "./MRStatusIcons";
import { MRComments } from "./MRComments";
import { AltLinkButton } from "./AltLinkButton";
import { CopyLinkButton } from "./CopyLinkButton";
import { StackChain } from "./StackChain";
import { JiraBadge } from "./JiraBadge";
import { StackIndicator } from "./StackIndicator";

export const ReviewBoardCard = ({
	mr,
	onDragStart,
	onDragEnd,
	isDragging,
	greyOutBlockingComments = true,
	enableDrag = true,
	enableCommentsExpansion = false,
}) => {
	const [useCodiff] = useCodiffLinks();

	const handleDragStart = (e) => {
		e.dataTransfer.setData("text/plain", mr.id);
		e.dataTransfer.effectAllowed = "move";
		if (onDragStart) onDragStart(mr);
	};

	const handleDragEnd = () => {
		if (onDragEnd) onDragEnd();
	};

	const isClosed = mr.state === "merged" || mr.state === "closed";
	const draggable = enableDrag && !isClosed;
	const stale = isStale(mr);

	// Check if this is my MR (author.isMe in gitdash data model)
	const isMine = mr.author?.isMe ?? false;

	const hasBlockingComments =
		greyOutBlockingComments &&
		mr.discussions > 0 &&
		mr.hasHumanReview &&
		!isMine &&
		!needsReviewerFollowUp(mr);

	return (
		<div
			data-mr-card={mr.id}
			draggable={draggable}
			onDragStart={draggable ? handleDragStart : undefined}
			onDragEnd={draggable ? handleDragEnd : undefined}
			className={`
				bg-[var(--color-bg-primary)] rounded-lg p-4 mb-3 
				border border-[var(--color-border)]
				hover:border-[var(--color-border-hover)]
				transition-all duration-200
				${mr.stack?.isBlockedByStack ? "ml-3" : ""}
				${draggable ? "cursor-grab active:cursor-grabbing" : "cursor-default"}
				${isDragging ? "opacity-50 scale-95" : ""}
				${(stale || mr.draft || hasBlockingComments || mr.stack?.isBlockedByStack) && !isClosed ? "opacity-50" : ""}
			`}
		>
			{/* Header: Avatar + Title */}
			<div className="flex gap-3">
				<Tooltip content={mr.author?.name || "Unknown Author"}>
					<Avatar
						src={mr.author?.avatarUrl}
						alt={mr.author?.name}
						size="md"
						className="flex-shrink-0"
					/>
				</Tooltip>

				<div className="flex-1 min-w-0">
					{/* Title */}
					<a
						href={mrLinkUrl(mr.webUrl, useCodiff)}
						target="_blank"
						rel="noopener noreferrer"
						className="text-sm font-semibold text-[var(--color-link)] hover:underline line-clamp-2"
						onClick={(e) => e.stopPropagation()}
						draggable="false"
					>
						{mr.title}
					</a>

					{/* Project + MR number + Status icons */}
					<div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-[var(--color-text-secondary)] mt-1">
						<a
							href={mr.webUrl?.split("/-/")[0]}
							target="_blank"
							rel="noopener noreferrer"
							draggable="false"
							className="hover:underline"
						>
							{formatProjectName(mr.projectPath)}
						</a>
						<JiraBadge mr={mr} />
						<MRStatusIcons mr={mr} iconClass="w-4 h-4" />
					</div>
				</div>
			</div>

			<StackChain mr={mr} className="mt-2" />

			{/* Status row */}
			<div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 mt-3 pt-3 border-t border-[var(--color-border)]">
				{/* Left: Status icons */}
				<div className="flex flex-col items-start gap-1">
					<div className="flex flex-wrap items-center gap-2">
						{!isClosed && <PipelineStatus pipeline={mr.headPipeline} />}

						{mr.userNotesCount > 0 && (
							<Tooltip content={`${mr.userNotesCount} comments`}>
								<span className="inline-flex items-center gap-1 text-[var(--color-text-secondary)]">
									<MessageSquare className="w-4 h-4" />
									<span className="text-xs">{mr.userNotesCount}</span>
								</span>
							</Tooltip>
						)}

						{/* State is shown as a labelled icon, not a coloured card edge. */}
						{mr.state === "merged" && (
							<span className="inline-flex items-center gap-1 text-xs font-medium text-[var(--status-ready-text)]">
								<GitMerge className="w-4 h-4" aria-hidden="true" />
								Merged
							</span>
						)}

						{mr.state === "closed" && (
							<span className="inline-flex items-center gap-1 text-xs font-medium text-[var(--status-danger-text)]">
								<GitPullRequestClosed className="w-4 h-4" aria-hidden="true" />
								Closed
							</span>
						)}

						{isMine && !isClosed && (
							<span
								className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium text-[var(--color-text-secondary)] ring-1 ring-inset ring-[var(--color-border)]"
								title="You authored this MR"
							>
								<User className="w-3.5 h-3.5" aria-hidden="true" />
								Mine
							</span>
						)}

						<DraftIcon draft={mr.draft} iconClass="w-4 h-4" />

						<AltLinkButton mr={mr} />
						<CopyLinkButton mr={mr} />
					</div>
					<StackIndicator mr={mr} />
				</div>

				{/* Right: Approval status + timestamps */}
				<div className="ml-auto flex items-center gap-3">
					{!isClosed && (
						<ApprovalStatus
							aiReviewMissing={mr.aiReviewMissing}
							approvers={mr.approvers}
							reviewers={mr.reviewers}
							approvalsLeft={mr.approvalsLeft}
						/>
					)}
					<span className="text-xs text-[var(--color-text-secondary)]">
						<DateTime date={mr.updatedAt} />
					</span>
				</div>
			</div>

			{enableCommentsExpansion && <MRComments mrId={mr.id} className="mt-2" />}
		</div>
	);
};
